import { z } from "zod";
import type { BisonEnvelope, SellerIdentity } from "./bison-contracts.ts";
import { personalName } from "./rules/text-helpers.ts";

/**
 * Who is writing, as a per-message thread state rather than an intent.
 *
 * A delegate's message can be `interested_engaged`, `price_probe` or `not_interested_soft` and still
 * be a substitution, so forcing identity into the intent enum makes the classifier choose between
 * two true labels. Four of these five fields are settled by the headers; `can_decide` is the one
 * that is not, and this resolver deliberately leaves it `unknown` rather than inventing it.
 */
export const ThreadIdentitySchema = z.strictObject({
  writer: z.enum(["campaign_lead", "lead_side_colleague", "third_party", "seller_side", "unknown"]),
  lead_has_written: z.boolean(),
  lead_still_addressed: z.boolean(),
  route: z.enum([
    "none",
    "named_no_address",
    "named_with_address",
    "arrived_unannounced",
    "unknown",
  ]),
  can_decide: z.enum(["yes", "no_conduit", "unknown"]),
  evidence: z.array(z.strictObject({ id: z.string().min(1), basis: z.string().min(1) })),
});
export type ThreadIdentity = z.infer<typeof ThreadIdentitySchema>;

const text = (value: unknown): string => (typeof value === "string" ? value : "");
const address = (value: unknown): string => text(value).trim().toLowerCase();
const domainOf = (value: string): string => value.split("@")[1] ?? "";
const localOf = (value: string): string => (value.split("@")[0] ?? "").replace(/[^a-z]/g, "");

/**
 * Sharing a public mailbox provider is not evidence of sharing an employer. The list is the common
 * providers rather than an exhaustive one: an unlisted provider only costs a `lead_side_colleague`
 * label where `unknown` would have been more honest, which is the safe direction to be wrong in.
 */
const consumerDomains = new Set([
  "gmail.com",
  "googlemail.com",
  "yahoo.com",
  "yahoo.co.uk",
  "hotmail.com",
  "hotmail.co.uk",
  "outlook.com",
  "live.com",
  "msn.com",
  "aol.com",
  "icloud.com",
  "me.com",
  "mac.com",
  "proton.me",
  "protonmail.com",
  "gmx.com",
  "gmx.net",
  "mail.com",
  "yandex.com",
  "zoho.com",
  "fastmail.com",
  "hey.com",
  "web.de",
  "wp.pl",
  "o2.pl",
  "interia.pl",
  "onet.pl",
]);

/** A public mailbox provider names no seller: listing one would make every lead on it the seller. */
export const isConsumerDomain = (domain: string): boolean =>
  consumerDomains.has(domain.trim().toLowerCase());

/** The union of several sources, normalised, sorted and without duplicates. */
export function mergeSellerIdentities(...parts: readonly SellerIdentity[]): SellerIdentity {
  const addresses = new Set<string>(),
    domains = new Set<string>();
  for (const part of parts) {
    for (const value of part.addresses) if (address(value)) addresses.add(address(value));
    for (const value of part.domains) if (address(value)) domains.add(address(value));
  }
  return { addresses: [...addresses].sort(), domains: [...domains].sort() };
}

/** True when the address is one the seller writes from. An empty address is nobody's. */
export function isSellerAddress(
  candidate: string | null | undefined,
  seller: SellerIdentity,
): boolean {
  const value = address(candidate);
  if (!value.includes("@")) return false;
  if (seller.addresses.some((entry) => address(entry) === value)) return true;
  const domain = domainOf(value);
  return seller.domains.some((entry) => address(entry) === domain);
}

/** `acme.com` and `acmehq.com` are one organisation; the affixes are the observed sending-domain forms. */
const organisationCore = (domain: string): string =>
  (domain.split(".")[0] ?? "")
    .replace(/^(?:try|get|go|use|the|my|join|hey|mail|email|inbox|send)/, "")
    .replace(/(?:hq|dev|app|mail|team|group|co|inc|io|now|ai|platform|labs|online|digital)$/, "")
    .replace(/[^a-z0-9]/g, "");

/** `true` same organisation, `false` demonstrably not, `null` the domains cannot settle it. */
function sameOrganisation(candidate: string, lead: string): boolean | null {
  const candidateDomain = domainOf(candidate),
    leadDomain = domainOf(lead);
  if (!candidateDomain || !leadDomain) return null;
  if (consumerDomains.has(candidateDomain) || consumerDomains.has(leadDomain)) return null;
  if (candidateDomain === leadDomain) return true;
  const core = organisationCore(leadDomain);
  return core.length >= 4 && organisationCore(candidateDomain) === core;
}

/**
 * One domain's brand label extends the other's: `acme` and `acmegroupeurope`, `acmegroup` and `acme`.
 *
 * Organisations that send outbound often run several domains, and the extra word is a real word more
 * often than a known affix, so this is deliberately not a same-organisation test: an unrelated business
 * can carry another's name inside its own. It only says the headers cannot rule the organisation out,
 * which is what `docs/sibling-writer-rule-preregistration-2026-09-15.md` hands to the person evidence.
 */
function siblingDomains(candidate: string, lead: string): boolean {
  const [candidateDomain, leadDomain] = [domainOf(candidate), domainOf(lead)];
  if (!candidateDomain || !leadDomain || candidateDomain === leadDomain) return false;
  if (consumerDomains.has(candidateDomain) || consumerDomains.has(leadDomain)) return false;
  const label = (domain: string) => (domain.split(".")[0] ?? "").replace(/[^a-z0-9]/g, "");
  const [a, b] = [label(candidateDomain), label(leadDomain)];
  if (a === b) return false;
  const [shorter, longer] = a.length <= b.length ? [a, b] : [b, a];
  return shorter.length >= 4 && (longer.startsWith(shorter) || longer.endsWith(shorter));
}

const lettersOf = (value: string): string => value.toLowerCase().replace(/[^\p{L}]/gu, "");

/** Person evidence strong enough to carry a sibling domain: a full name, or a named mailbox. */
function siblingPersonBasis(
  candidate: string,
  displayName: string,
  lead: string,
  leadFirst: string,
  leadLast: string,
): string | null {
  const [first, last] = [lettersOf(leadFirst), lettersOf(leadLast)];
  if (first.length >= 2 && last.length >= 2 && lettersOf(displayName) === `${first}${last}`)
    return "the writer's display name is the lead of record's full name";
  const candidateLocal = localOf(candidate);
  if (last.length >= 3 && candidateLocal === localOf(lead) && candidateLocal.includes(last))
    return "an identical mailbox name carrying the lead of record's last name";
  return null;
}

/**
 * Positive evidence that a second address belongs to the lead themselves, never absence of evidence.
 * About half the population that reads as "a colleague replied" is this — `noam@` answering a thread
 * addressed to `noam.rosenfeld@` — and calling it a colleague hands the classifier a person who does
 * not exist. Each test under-claims: the length floors keep an initials mailbox from collapsing into
 * whoever it happens to prefix.
 */
function aliasBasis(
  candidate: string,
  displayName: string,
  lead: string,
  leadFirst: string,
  leadLast: string,
): string | null {
  const candidateLocal = localOf(candidate),
    leadLocal = localOf(lead);
  const writerFirst = (personalName(displayName) ?? "").toLowerCase();
  const first = leadFirst.trim().toLowerCase(),
    last = leadLast.trim().toLowerCase();
  if (writerFirst && first && writerFirst === first)
    return "the writer's display first name matches the lead of record";
  if (
    leadLocal.length >= 4 &&
    candidateLocal.length >= 4 &&
    (candidateLocal.includes(leadLocal) || leadLocal.includes(candidateLocal))
  )
    return "one address local part contains the other";
  if (
    first.length >= 3 &&
    last.length >= 3 &&
    candidateLocal.includes(first) &&
    candidateLocal.includes(last)
  )
    return "the writer's local part carries the lead's full name";
  return null;
}

/** Pure: the writer classification and the one sentence explaining how it was reached. */
function resolveWriter(
  writerAddress: string,
  displayName: string,
  leadAddress: string,
  leadFirst: string,
  leadLast: string,
  seller: SellerIdentity,
): { writer: ThreadIdentity["writer"]; basis: string } {
  if (!writerAddress)
    return { writer: "unknown", basis: "the message being answered carries no sender address" };
  if (isSellerAddress(writerAddress, seller))
    return {
      writer: "seller_side",
      basis: "the writer's address is one the seller writes from",
    };
  if (!leadAddress)
    return { writer: "unknown", basis: "no lead of record to resolve the writer against" };
  if (writerAddress === leadAddress)
    return { writer: "campaign_lead", basis: "the writer's address is the lead of record" };

  const side = sameOrganisation(writerAddress, leadAddress);
  if (side === null)
    return {
      writer: "unknown",
      basis: "the writer's domain carries no organisational information about the lead",
    };
  if (side === false) {
    if (!siblingDomains(writerAddress, leadAddress))
      return { writer: "third_party", basis: "the writer's organisation differs from the lead's" };
    const person = siblingPersonBasis(writerAddress, displayName, leadAddress, leadFirst, leadLast);
    return person
      ? {
          writer: "campaign_lead",
          basis: `a second address for the lead of record on a sibling domain: ${person}`,
        }
      : {
          writer: "unknown",
          basis:
            "the writer's domain extends the lead's, and the headers cannot settle the organisation",
        };
  }

  const alias = aliasBasis(writerAddress, displayName, leadAddress, leadFirst, leadLast);
  if (alias)
    return { writer: "campaign_lead", basis: `a second address for the lead of record: ${alias}` };
  return {
    writer: "lead_side_colleague",
    basis: "the writer shares the lead's organisation but is not the lead",
  };
}

export type ParticipantContext = Readonly<{
  leadAddress: string;
  leadFirst: string;
  leadLast: string;
  seller: SellerIdentity;
}>;

/** The thread-wide facts every per-address classification is made against, derived once. */
export function participantContext(input: BisonEnvelope): ParticipantContext {
  return {
    leadAddress: address(input.lead.email),
    leadFirst: text(input.lead.first_name),
    leadLast: text(input.lead.last_name),
    // The sending mailbox always, and every other address or domain the import resolved as the seller's.
    seller: mergeSellerIdentities(input.seller_identity, {
      addresses: [input.sender.email, input.sender["email_address"]].map(address).filter(Boolean),
      domains: [],
    }),
  };
}

export type ParticipantIdentity = Readonly<{
  side: "lead_side" | "seller_side" | "third_party" | "unknown";
  is_campaign_target: boolean;
}>;

const sideOf: Record<ThreadIdentity["writer"], ParticipantIdentity["side"]> = {
  campaign_lead: "lead_side",
  lead_side_colleague: "lead_side",
  seller_side: "seller_side",
  third_party: "third_party",
  unknown: "unknown",
};

/**
 * Which side someone is on, and separately whether they are the contact the campaign enrolled.
 * Those are two facts that coincide in most threads and come apart in exactly the ones worth
 * getting right, so a single `recipient` role could not carry both.
 */
export function classifyParticipant(
  candidate: string,
  displayName: string,
  context: ParticipantContext,
): ParticipantIdentity {
  const { writer } = resolveWriter(
    address(candidate),
    displayName,
    context.leadAddress,
    context.leadFirst,
    context.leadLast,
    context.seller,
  );
  return { side: sideOf[writer], is_campaign_target: writer === "campaign_lead" };
}

/**
 * FNV-1a, 64-bit. Not a security primitive and not trying to be — it exists so an address does not
 * become a database key, while staying recomputable from that address so a future deletion path can
 * still find every record about one person. Written out rather than imported because everything
 * reachable from the pipeline has to survive a sandbox with no Node builtins.
 */
function fingerprint(value: string): string {
  let hash = 0xcbf2_9ce4_8422_2325n;
  for (const unit of value) {
    hash ^= BigInt(unit.codePointAt(0) ?? 0);
    hash = (hash * 0x0000_0100_0000_01b3n) & 0xffff_ffff_ffff_ffffn;
  }
  return hash.toString(16).padStart(16, "0");
}

/**
 * A stable handle for the human who wrote the message being answered, for use as a context scope.
 *
 * The enrolled contact resolves to their lead record, which is already a stable non-personal handle
 * and, because the writer resolution is alias-aware, covers every address they write from. Anyone
 * else resolves to a fingerprint of their own address. Returns null when the writer cannot be
 * identified: a scope that might mean anyone is worse than no scope at all.
 */
function participantScopeId(
  input: BisonEnvelope,
  email: string | null | undefined,
  name: string | null | undefined,
): string | null {
  const writerAddress = address(email);
  if (!writerAddress) return null;
  const { leadAddress, leadFirst, leadLast, seller } = participantContext(input);
  const { writer } = resolveWriter(
    writerAddress,
    text(name),
    leadAddress,
    leadFirst,
    leadLast,
    seller,
  );
  const leadId = input.lead.id;
  if (writer === "campaign_lead" && (typeof leadId === "number" || typeof leadId === "string"))
    return `lead:${leadId}`;
  return `person:${fingerprint(writerAddress)}`;
}

/** Current writer identity, retaining the historical alias policy. */
export function personScopeId(input: BisonEnvelope): string | null {
  return participantScopeId(input, input.trigger.from_email_address, input.trigger.from_name);
}

/** Message authors cannot borrow the current writer's display name to establish an alias. */
export function messagePersonScopeId(
  input: BisonEnvelope,
  message: BisonEnvelope["messages"][number],
): string | null {
  const author = address(message.from_email_address);
  if (author && author === address(input.trigger.from_email_address)) return personScopeId(input);
  return participantScopeId(input, message.from_email_address, null);
}

export function resolveThreadIdentity(input: BisonEnvelope): ThreadIdentity {
  const writerAddress = address(input.trigger.from_email_address);
  const { leadAddress, leadFirst, leadLast, seller } = participantContext(input);
  const triggerMessage =
    input.messages.find(
      (message) => message.provider_id === input.trigger.id && message.direction === "inbound",
    ) ?? [...input.messages].reverse().find((message) => message.direction === "inbound");
  const evidence: ThreadIdentity["evidence"] = [];
  const note = (id: string | undefined, basis: string) => {
    if (id) evidence.push({ id, basis });
  };

  /** Exact match, or an alias provable from the address alone — messages carry no display name. */
  const isLead = (candidate: string): boolean => {
    if (!candidate || !leadAddress) return false;
    if (candidate === leadAddress) return true;
    if (sameOrganisation(candidate, leadAddress) === false)
      return (
        siblingDomains(candidate, leadAddress) &&
        siblingPersonBasis(candidate, "", leadAddress, leadFirst, leadLast) !== null
      );
    return (
      sameOrganisation(candidate, leadAddress) === true &&
      aliasBasis(candidate, "", leadAddress, leadFirst, leadLast) !== null
    );
  };

  const { writer, basis } = resolveWriter(
    writerAddress,
    text(input.trigger.from_name),
    leadAddress,
    leadFirst,
    leadLast,
    seller,
  );
  note(triggerMessage?.id, basis);

  const leadMessage = [...input.messages]
    .filter((message) => message.direction === "inbound")
    .sort((a, b) => Date.parse(a.date_received) - Date.parse(b.date_received))
    .find((message) => isLead(address(message.from_email_address)));
  const lead_has_written = writer === "campaign_lead" || leadMessage !== undefined;
  if (leadMessage) note(leadMessage.id, "the lead of record wrote this message");

  const addressedNow = [...(input.trigger.to ?? []), ...(input.trigger.cc ?? [])].map((party) =>
    address(party.address),
  );
  const lead_still_addressed = writer === "campaign_lead" || addressedNow.some(isLead);

  let route: ThreadIdentity["route"] = "unknown";
  if (writer === "campaign_lead" || writer === "seller_side") route = "none";
  else if (writer !== "unknown") {
    const ordered = [...input.messages].sort(
      (a, b) => Date.parse(a.date_received) - Date.parse(b.date_received),
    );
    const firstWrote = ordered.findIndex(
      (message) => address(message.from_email_address) === writerAddress,
    );
    const introduced = ordered
      .slice(0, firstWrote === -1 ? ordered.length : firstWrote)
      .find((message) =>
        [...(message.to ?? []), ...(message.cc ?? [])].some(
          (party) => address(party.address) === writerAddress,
        ),
      );
    route = introduced ? "named_with_address" : "arrived_unannounced";
    if (introduced) note(introduced.id, "the writer's address was on the thread before they wrote");
  }

  return ThreadIdentitySchema.parse({
    writer,
    lead_has_written,
    lead_still_addressed,
    route,
    // The one field the headers cannot settle. Deriving it here would hand the classifier a
    // confident value that nothing measured.
    can_decide: "unknown",
    evidence,
  });
}
